import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Form, useForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';

import FormNameContext from './FormNameContext';
import { registerForm } from './registry';
import { destroyFormState, updateFormState } from './reducer';
import isFormValueEqual from './formEquality';


// Higher-order component with the redux-form `reduxForm(config)(Component)`
// API implemented with react-final-form. The wrapped components keep their
// redux-form props: handleSubmit, submitting, dirty, pristine, invalid,
// submitSucceeded, submitFailed, initialValues, change, reset, form...
//
// Supported config (also as props): form, validate(values, props), onSubmit,
// onChange(values, dispatch, props, previousValues), initialValues,
// enableReinitialize, keepDirtyOnReinitialize, destroyOnUnmount.

const CONFIG_KEYS = [
  'form',
  'validate',
  'onSubmit',
  'onChange',
  'initialValues',
  'enableReinitialize',
  'keepDirtyOnReinitialize',
  'destroyOnUnmount',
];

const FORM_SUBSCRIPTION = {
  submitting: true,
  submitSucceeded: true,
  submitFailed: true,
  invalid: true,
  valid: true,
  values: true,
  initialValues: true,
  error: true,
  touched: true,
};

const EMPTY_VALUES = {};

const pickConfig = (config, props) => CONFIG_KEYS.reduce((result, key) => (
  props[key] === undefined ? result : { ...result, [key]: props[key] }
), config);

const FormBody = ({
  WrappedComponent,
  formName,
  formState,
  handleSubmit,
  ownProps,
  settings,
  setCustomSubmit,
}) => {
  const dispatch = useDispatch();
  // The form instance from the context is stable (the `form` render prop of
  // <Form> is a new object on every render).
  const formApi = useForm('reduxForm');
  const [submitFailedCleared, setSubmitFailedCleared] = useState(false);
  const injectedRef = useRef(null);

  const {
    values = EMPTY_VALUES,
    initialValues = EMPTY_VALUES,
    submitting,
    submitSucceeded,
    submitFailed,
    invalid,
    valid,
    error,
    touched,
  } = formState;

  const dirty = !isFormValueEqual(values, initialValues);

  const submitForm = (eventOrSubmit) => {
    if (typeof eventOrSubmit === 'function') {
      return (event) => {
        if (event && typeof event.preventDefault === 'function') {
          event.preventDefault();
        }
        setCustomSubmit(eventOrSubmit);
        setSubmitFailedCleared(false);
        return formApi.submit();
      };
    }
    setSubmitFailedCleared(false);
    return handleSubmit(eventOrSubmit);
  };

  const injectedProps = {
    ...ownProps,
    form: formName,
    initialValues,
    handleSubmit: submitForm,
    submitting: !!submitting,
    submitSucceeded: !!submitSucceeded,
    submitFailed: !!submitFailed && !submitFailedCleared,
    invalid: !!invalid,
    valid: !!valid,
    dirty,
    pristine: !dirty,
    anyTouched: !!(touched && Object.values(touched).some(Boolean)),
    error,
    dispatch,
    change: (field, value) => formApi.change(field, value),
    blur: field => formApi.blur(field),
    focus: field => formApi.focus(field),
    reset: () => {
      setSubmitFailedCleared(false);
      formApi.reset();
    },
    submit: () => formApi.submit(),
    initialize: newValues => formApi.initialize(newValues),
    clearSubmit: () => setSubmitFailedCleared(true),
    destroy: () => {},
    array: {
      push: (field, value) => formApi.mutators.push(field, value),
      remove: (field, index) => formApi.mutators.remove(field, index),
      insert: (field, index, value) => formApi.mutators.insert(field, index, value),
      move: (field, from, to) => formApi.mutators.move(field, from, to),
    },
  };
  injectedRef.current = injectedProps;

  // Register the form API so that action creators and selectors can reach it
  // by name. The first registration happens during the render, before the
  // children render: redux-form initialized the form state before its fields
  // rendered and some connected children read it in mapStateToProps.
  const createEntry = () => ({
    api: formApi,
    submit: () => {
      setSubmitFailedCleared(false);
      return formApi.submit();
    },
    reset: () => {
      setSubmitFailedCleared(false);
      formApi.reset();
    },
    clearSubmitFailed: () => setSubmitFailedCleared(true),
  });
  const registeredRef = useRef(null);
  if (!registeredRef.current || registeredRef.current.formName !== formName) {
    registeredRef.current = { formName };
    registerForm(formName, createEntry());
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => registerForm(formName, createEntry()), [formApi, formName]);

  // Publish the form state to redux (state.form[formName]) for selectors.
  useEffect(() => {
    const unsubscribe = formApi.subscribe((state) => {
      const currentValues = state.values || EMPTY_VALUES;
      const currentInitial = state.initialValues || EMPTY_VALUES;
      dispatch(updateFormState(formName, {
        values: currentValues,
        initial: currentInitial,
        dirty: !isFormValueEqual(currentValues, currentInitial),
        submitting: !!state.submitting,
        submitSucceeded: !!state.submitSucceeded,
        submitFailed: !!state.submitFailed,
        invalid: !!state.invalid,
      }));
    }, {
      values: true,
      initialValues: true,
      submitting: true,
      submitSucceeded: true,
      submitFailed: true,
      invalid: true,
    });
    return () => {
      unsubscribe();
      if (settings.destroyOnUnmount !== false) {
        dispatch(destroyFormState(formName));
      }
    };
  }, [formApi, formName, dispatch, settings.destroyOnUnmount]);

  // config.onChange(values, dispatch, props, previousValues)
  const onChangeRef = useRef(settings.onChange);
  onChangeRef.current = settings.onChange;
  useEffect(() => {
    let previousValues;
    let initialized = false;
    return formApi.subscribe(({ values: currentValues }) => {
      if (!initialized) {
        initialized = true;
        previousValues = currentValues;
        return;
      }
      if (onChangeRef.current && !isEqual(currentValues, previousValues)) {
        onChangeRef.current(currentValues, dispatch, injectedRef.current, previousValues);
      }
      previousValues = currentValues;
    }, { values: true });
  }, [formApi, dispatch]);

  return (
    <FormNameContext.Provider value={formName}>
      <WrappedComponent {...injectedProps} />
    </FormNameContext.Provider>
  );
};

const reduxForm = (config = {}) => (WrappedComponent) => {
  const ReduxForm = (props) => {
    const settings = pickConfig(config, props);
    const formName = settings.form;
    const ownPropsRef = useRef(props);
    ownPropsRef.current = props;
    const customSubmitRef = useRef(null);
    const dispatch = useDispatch();

    // Without enableReinitialize redux-form used only the first initialValues.
    const [firstInitialValues] = useState(settings.initialValues);
    const initialValues = settings.enableReinitialize ? settings.initialValues : firstInitialValues;
    const initialValuesRef = useRef(initialValues);
    initialValuesRef.current = initialValues;

    if (!formName) {
      throw new Error('reduxForm: the "form" name is required');
    }

    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    // Stable callbacks: final-form re-validates whenever `validate` changes.
    const validate = useCallback((values) => {
      const { validate: currentValidate } = settingsRef.current;
      if (!currentValidate) {
        return undefined;
      }
      return currentValidate(values, {
        ...ownPropsRef.current,
        initialValues: initialValuesRef.current || EMPTY_VALUES,
      });
    }, []);

    const onSubmit = useCallback((values) => {
      const submitHandler = customSubmitRef.current || settingsRef.current.onSubmit;
      customSubmitRef.current = null;
      if (!submitHandler) {
        throw new Error(`reduxForm "${settingsRef.current.form}": onSubmit is not specified`);
      }
      return submitHandler(values, dispatch, { ...ownPropsRef.current, form: settingsRef.current.form });
    }, [dispatch]);

    const setCustomSubmit = useCallback((submitHandler) => {
      customSubmitRef.current = submitHandler;
    }, []);

    return (
      <Form
        key={formName}
        onSubmit={onSubmit}
        initialValues={initialValues}
        initialValuesEqual={isEqual}
        keepDirtyOnReinitialize={!!settings.keepDirtyOnReinitialize}
        validate={validate}
        mutators={arrayMutators}
        subscription={FORM_SUBSCRIPTION}
        render={({ handleSubmit, form: ignoredFormApi, ...formState }) => (
          <FormBody
            WrappedComponent={WrappedComponent}
            formName={formName}
            formState={formState}
            handleSubmit={handleSubmit}
            ownProps={props}
            settings={settings}
            setCustomSubmit={setCustomSubmit}
          />
        )}
      />
    );
  };
  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  ReduxForm.displayName = `reduxForm(${wrappedName})`;
  return ReduxForm;
};

export default reduxForm;
